/* do not edit this unless you are bootstraping the project and know what you are doing */
import axios from "axios";
import { z } from "zod";

import i18n from "@/config/i18n";
import { logger } from "@/util/logger";
import { RestUtils } from "@/util/rest/rest.utils";

// codes that we want to handle in every scenario
export type GeneralErrorCodes =
  | "DATA_VALIDATION_ERROR"
  | "NETWORK_ERROR"
  | "CANCELED_ERROR"
  | "INTERNAL_ERROR"
  | "UNKNOWN_ERROR"
  | "ERR_BAD_REQUEST";

export class ApplicationException<CodeT> extends Error {
  public code: CodeT;

  public serverMessage: string | null = null;

  constructor(message: string, code: CodeT, serverMessage: string | null) {
    super(message);

    this.code = code;
    this.serverMessage = serverMessage;
  }
}

interface ErrorEntry<CodeT> {
  code: CodeT;
  condition: (error: unknown) => boolean;
  getMessage: (error: unknown) => string;
}

export class ErrorHandler<CodeT extends string> {
  entries: ErrorEntry<CodeT | GeneralErrorCodes>[] = [];

  constructor(entries: ErrorEntry<CodeT>[]) {
    type ICodeT = CodeT | GeneralErrorCodes;

    // implement checking for each of the general errors

    const quotaExceededError: ErrorEntry<ICodeT> = {
      code: "ERR_BAD_REQUEST",
      condition: (e) => {
        if (axios.isAxiosError(e) && e.response?.status === 429 && e.response?.data?.code === "quota_exceeded") {
          return true;
        }

        return false;
      },
      getMessage: (e) => {
        if (axios.isAxiosError(e) && e.response?.status === 429 && e.response?.data?.code === "quota_exceeded") {
          return e.response?.data?.message;
        }

        return "You have exceeded the quota for this action. Please try again tomorrow.";
      },
    };

    const dataValidationError: ErrorEntry<ICodeT> = {
      code: "DATA_VALIDATION_ERROR",
      condition: (e) => {
        return e instanceof z.ZodError;
      },
      getMessage: () => i18n.t("shared.sharedErrors.dataValidation"),
    };

    const internalError: ErrorEntry<ICodeT> = {
      code: "INTERNAL_ERROR",
      condition: (e) => {
        if (axios.isAxiosError(e)) {
          return e.response?.status != null && e.response.status >= 500 && e.response.status < 600;
        }

        return false;
      },
      getMessage: () => i18n.t("shared.sharedErrors.internalError"),
    };

    const networkError: ErrorEntry<ICodeT> = {
      code: "NETWORK_ERROR",
      condition: (e) => {
        if (axios.isAxiosError(e)) {
          return e.code === "ERR_NETWORK";
        }

        return false;
      },
      getMessage: () => i18n.t("shared.sharedErrors.networkError"),
    };

    const canceledError: ErrorEntry<ICodeT> = {
      code: "CANCELED_ERROR",
      condition: (e) => {
        if (axios.isCancel(e)) {
          return true;
        }

        if (axios.isAxiosError(e) && e.code === "ECONNABORTED") {
          return true;
        }

        return false;
      },
      getMessage: () => i18n.t("shared.sharedErrors.canceledError"),
    };

    const unknownError: ErrorEntry<ICodeT> = {
      code: "UNKNOWN_ERROR",
      condition: () => true,
      getMessage: () => i18n.t("shared.sharedErrors.unknownError"),
    };

    // general errors have the lowest priority
    this.entries = [
      ...entries,
      quotaExceededError,
      dataValidationError,
      internalError,
      networkError,
      canceledError,
      unknownError,
    ];
  }

  // convert the error into an application exception
  public rethrowError(error: unknown): ApplicationException<CodeT | GeneralErrorCodes> {
    logger.error(error);
    const errorEntry = this.entries.find((entry) => entry.condition(error ?? {}))!;

    const serverMessage = RestUtils.extractServerErrorMessage(error);
    if (errorEntry.code === "ERR_BAD_REQUEST") {
      throw new Error(errorEntry.getMessage(error), { cause: serverMessage });
    }
    throw new ApplicationException(errorEntry.getMessage(error), errorEntry.code, serverMessage);
  }

  // oxlint-disable-next-line class-methods-use-this
  public getError(error: unknown): ApplicationException<CodeT | GeneralErrorCodes> | null {
    if (error instanceof ApplicationException) {
      return error;
    }

    return null;
  }

  // oxlint-disable-next-line class-methods-use-this
  public getErrorCode(error: unknown): CodeT | GeneralErrorCodes | null {
    if (error instanceof ApplicationException) {
      return error.code;
    }

    return null;
  }

  public static getErrorMessage(error: unknown, fallbackToUnknown: boolean = true): string | null {
    if (typeof error === "string") {
      return error;
    }

    if (error instanceof Error) {
      return error.message;
    }

    if (error instanceof ApplicationException) {
      return error.message;
    }

    if (fallbackToUnknown) {
      return i18n.t("shared.sharedErrors.unknownError");
    }

    return null;
  }
}

// can be used for endpoints that only need general error handling
export const SharedErrorHandler = new ErrorHandler<never>([]);
